import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Dialog, Transition } from '@headlessui/react';
import { useTheme } from '../../context/ThemeContext';
import { authService } from '../../services/auth';
import { useSelector, useDispatch } from 'react-redux';
import { clearCart } from '../../store/slices/cartSlice';
import { hasPermission } from '../../utils/permissionsUtils';
import {
  MoonIcon, SunIcon, UserCircleIcon,
  LogoutIcon, ShoppingCartIcon, SearchIcon,
  ChevronDownIcon, CogIcon, ChatIcon, BookOpenIcon, CreditCardIcon, QuestionMarkCircleIcon,
  ViewGridIcon
} from '@heroicons/react/outline';
import LogoLight from '../../assets/images/logo-light.png';
import LogoDark from '../../assets/images/logo-dark.png';
import GuideSlider from '../guide/GuideSlider';

const Navbar = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { theme, toggleTheme } = useTheme();
  
  // قراءة بيانات المستخدم مباشرة لتجنب الرمش
  const initialUser = (() => {
    try {
      const storedUser = sessionStorage.getItem('user');
      return storedUser ? JSON.parse(storedUser) : null;
    } catch {
      return null;
    }
  })();
  
  const [user, setUser] = useState(initialUser);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showGuideModal, setShowGuideModal] = useState(false);
  const [showUsageModal, setShowUsageModal] = useState(false);
  const dropdownRef = useRef(null);
  // حساب عدد العناصر في السلة من خلال جمع الكمية لكل العناصر
  const cartItems = useSelector(state => state.cart.items);
  const cartItemsCount = cartItems.reduce((total, item) => total + (item.quantity || 0), 0);

  useEffect(() => {
    // لم نعد بحاجة للقراءة من sessionStorage هنا لأننا فعلنا ذلك مباشرة
    // إغلاق القائمة المنسدلة عند الضغط خارجها
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    // ESC key handler for modal
    const handleEscKey = (event) => {
      if (event.key === 'Escape' && showGuideModal) {
        setShowGuideModal(false);
      }
      if (event.key === 'Escape' && showUsageModal) {
        setShowUsageModal(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscKey);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [showGuideModal, showUsageModal]);

  useEffect(() => {
    const handleEsc = (event) => {
      if (event.key === 'Escape') {
        setShowSearch(false);
        setSearchQuery('');
      }
    };

    const handleClickOutside = (event) => {
      if (showSearch && !event.target.closest('.search-container')) {
        setShowSearch(false);
        setSearchQuery('');
      }
    };

    window.addEventListener('keydown', handleEsc);
    window.addEventListener('mousedown', handleClickOutside);

    return () => {
      window.removeEventListener('keydown', handleEsc);
      window.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showSearch]);

  const handleLogout = async () => {
    await authService.logout();
    // مسح السلة عند تسجيل الخروج
    dispatch(clearCart());
    setUser(null);
    navigate('/');
  };

  const handleSearch = async (value) => {
    setSearchQuery(value);
    if (value.length >= 2) {
      setIsSearching(true);
      try {
        const response = await fetch(`/api/search?q=${encodeURIComponent(value)}`);
        if (!response.ok) throw new Error('Search request failed');
        const results = await response.json();
        setSearchResults(results);
      } catch (error) {
        console.error('Error searching:', error);
      } finally {
        setIsSearching(false);
      }
    } else {
      setSearchResults([]);
    }
  };

  const menuItems = [
    { title: 'الصفحة الرئيسية', path: '/' },
    { title: 'الدورات', path: '/courses' },
    { title: 'المتجر', path: '/store' },
  ];

  // قراءة صلاحيات المستخدم من sessionStorage
  let userPermissions = [];
  try {
    const storedPermissions = sessionStorage.getItem('userPermissions');
    userPermissions = storedPermissions ? JSON.parse(storedPermissions) : [];
  } catch (error) {
    console.error('خطأ في قراءة صلاحيات المستخدم:', error);
  }

  // إنشاء قائمة الخيارات الأساسية للمستخدمين العاديين
  const baseMenuItems = user && !userPermissions.includes('access_dashboard') ? [
    { title: 'الإعدادات', path: '/profile', icon: CogIcon },
    { title: 'دوراتي', path: '/profile?tab=courses', icon: BookOpenIcon },
    { title: 'اسأل مدرب', path: '/ask-instructor', icon: QuestionMarkCircleIcon },
    { title: 'تسجيل الخروج', onClick: handleLogout, icon: LogoutIcon }
  ] : [
    { title: 'الإعدادات', path: '/profile', icon: CogIcon },
    { title: 'تسجيل الخروج', onClick: handleLogout, icon: LogoutIcon }
  ];
  
  // إضافة خيار لوحة التحكم للمستخدمين الذين لديهم صلاحية access_dashboard
  const userMenuItems = [];
  
  // التحقق من وجود صلاحية الوصول للوحة التحكم
  if (user && userPermissions.includes('access_dashboard')) {
    userMenuItems.push({ title: 'لوحة التحكم', path: '/admin/dashboard', icon: ViewGridIcon });
  }
  
  // إضافة الخيارات الأساسية
  userMenuItems.push(...baseMenuItems);

  return (
    <>
    <nav className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 fixed w-full top-0 z-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          {/* Logo */}
          <div className="flex-shrink-0 flex items-center">
            <Link to="/">
              <img
                className="h-12 w-auto"
                src={theme === 'dark' ? LogoDark : LogoLight}
                alt="دوز التعليمية" 
              />
            </Link>
          </div>

          {/* Desktop Menu */}
          <div className="hidden md:flex md:items-center md:space-x-4 md:space-x-reverse">
            {menuItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className="text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white px-3 py-2 text-sm font-medium"
              >
                {item.title}
              </Link>
            ))}
            <button
              onClick={() => setShowUsageModal(true)}
              className="text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white px-3 py-2 text-sm font-medium"
            >
              دليل الاستخدام
            </button>
          </div>

          {/* Right Side Items */}
          <div className="flex items-center space-x-4 space-x-reverse">
            <button
              onClick={() => setShowSearch(!showSearch)}
              className="text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 p-2 rounded-lg"
            >
              <SearchIcon className="h-6 w-6" />
            </button>

            <Link to="/cart" className="relative">
              <ShoppingCartIcon className="h-6 w-6 text-gray-500 dark:text-gray-400" />
              {cartItemsCount > 0 && (
                <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs w-5 h-5 flex items-center justify-center rounded-full">
                  {cartItemsCount}
                </span>
              )}
            </Link>

            <button
              onClick={toggleTheme}
              className="text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 p-2 rounded-lg"
            >
              {theme === 'dark' ? (
                <SunIcon className="h-6 w-6 text-yellow-400 transition-colors duration-200" />
              ) : (
                <MoonIcon className="h-6 w-6 text-indigo-500 dark:text-indigo-400 transition-colors duration-200" />
              )}
            </button>

            {/* Desktop Auth */}
            <div className="hidden md:flex md:items-center md:space-x-2 md:space-x-reverse">
              {!user ? (
                <>
                  <Link
                    to="/login"
                    className="text-black bg-gradient-to-l from-[#fad94a] to-[#f4bf49] hover:from-[#d4b83f] hover:to-[#cfa13d] px-4 py-2 rounded-full text-sm font-medium"
                  >
                    تسجيل دخول
                  </Link>
                  <Link
                    to="/register"
                    className="text-primary-600 border border-primary-600 hover:bg-gray-50 dark:text-white dark:border-white dark:hover:bg-gray-800 px-4 py-2 rounded-full text-sm font-medium"
                  >
                    إنشاء حساب
                  </Link>
                </>
              ) : (
                <div className="relative" ref={dropdownRef}>
                  <button
                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                    onMouseEnter={() => setIsDropdownOpen(true)}
                    className="flex items-center space-x-3 space-x-reverse text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
                  >
                    {user.avatar ? (
                      <div className="relative h-8 w-8">
                        <img 
                          src={`/images/avatars/${user.avatar}.png`} 
                          alt="صورة المستخدم" 
                          className="h-8 w-8 rounded-full object-cover"
                          onError={(e) => {
                            // في حالة فشل تحميل الصورة، نعرض الأيقونة الافتراضية
                            e.target.style.display = 'none';
                            e.target.nextElementSibling.style.display = 'block';
                          }}
                        />
                        <UserCircleIcon className="h-8 w-8 hidden absolute top-0 left-0" />
                      </div>
                    ) : (
                      <UserCircleIcon className="h-8 w-8" />
                    )}
                    <span className="text-sm font-medium">{user.name}</span>
                    <ChevronDownIcon className="h-5 w-5" />
                  </button>

                  {isDropdownOpen && (
                    <div 
                      className="absolute left-0 mt-2 w-48 rounded-md shadow-lg bg-white dark:bg-gray-800 ring-1 ring-black ring-opacity-5"
                      onMouseLeave={() => setIsDropdownOpen(false)}
                    >
                      <div className="py-1">
                        {userMenuItems.map((item, index) => (
                          <div key={index}>
                            {item.onClick ? (
                              <button
                                onClick={item.onClick}
                                className="w-full flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                              >
                                <item.icon className="h-5 w-5 ml-2" />
                                {item.title}
                              </button>
                            ) : (
                              <button
                                onClick={() => {
                                  setIsDropdownOpen(false);
                                  window.location.href = item.path;
                                }}
                                className="w-full flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                              >
                                <item.icon className="h-5 w-5 ml-2" />
                                {item.title}
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              className="text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 p-2 rounded-lg md:hidden"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <div className="md:hidden">
        {/* Overlay */}
        <div 
          className={`fixed inset-0 bg-black transition-opacity duration-300 ${
            isDropdownOpen ? 'opacity-50 pointer-events-auto' : 'opacity-0 pointer-events-none'
          }`} 
          onClick={() => setIsDropdownOpen(false)} 
        />
        
        {/* Sidebar */}
        <div 
          className={`fixed inset-y-0 left-0 w-64 bg-white dark:bg-gray-800 shadow-lg transform transition-transform duration-300 ease-in-out ${
            isDropdownOpen ? 'translate-x-0' : '-translate-x-full'
          }`}
        >
          <div className="flex flex-col h-full">
            {/* Menu Header */}
            <div className="flex items-center justify-end p-4 border-b border-gray-200 dark:border-gray-700">
              <button
                onClick={() => setIsDropdownOpen(false)}
                className="text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 p-2 rounded-lg"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Menu Items */}
            <div className="flex-1 overflow-y-auto py-4">
              {menuItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className="block px-4 py-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                  onClick={() => setIsDropdownOpen(false)}
                >
                  {item.title}
                </Link>
              ))}
              
              {/* دليل الاستخدام للجوال */}
              <button
                onClick={() => {
                  setShowUsageModal(true);
                  setIsDropdownOpen(false);
                }}
                className="w-full text-right px-4 py-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                دليل الاستخدام
              </button>

              {/* Auth Buttons */}
              {!user && (
                <div className="px-4 mt-2 space-y-2">
                  <Link
                    to="/login"
                    className="block w-full px-4 py-2 text-center text-black bg-gradient-to-l from-[#fad94a] to-[#f4bf49] hover:from-[#d4b83f] hover:to-[#cfa13d] rounded-full font-medium transition-colors"
                    onClick={() => setIsDropdownOpen(false)}
                  >
                    تسجيل دخول
                  </Link>
                  <Link
                    to="/register"
                    className="block w-full px-4 py-2 text-center text-primary-600 border border-primary-600 bg-white hover:bg-gray-50 dark:bg-transparent dark:text-white dark:border-white dark:hover:bg-gray-800 rounded-full font-medium transition-colors"
                    onClick={() => setIsDropdownOpen(false)}
                  >
                    إنشاء حساب
                  </Link>
                </div>
              )}
            </div>

            {/* User Menu Items */}
            {user && (
              <div className="p-4 border-t border-gray-200 dark:border-gray-700">
                <div className="space-y-2">
                  {userMenuItems.map((item, index) => (
                    <div key={index}>
                      {item.onClick ? (
                        <button
                          onClick={() => {
                            item.onClick();
                            setIsDropdownOpen(false);
                          }}
                          className="w-full flex items-center px-4 py-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                        >
                          <item.icon className="h-5 w-5 ml-2" />
                          {item.title}
                        </button>
                      ) : (
                        <button
                          onClick={() => {
                            setIsDropdownOpen(false);
                            window.location.href = item.path;
                          }}
                          className="w-full flex items-center px-4 py-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                        >
                          <item.icon className="h-5 w-5 ml-2" />
                          {item.title}
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Search Modal */}
      {showSearch && (
        <div className="fixed inset-0 z-50">
          <div className="fixed inset-0 bg-black bg-opacity-50" onClick={() => setShowSearch(false)} />
          <div className="relative z-50 flex items-center justify-center min-h-screen p-4">
            <div className="w-full max-w-3xl">
              <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-300 dark:border-gray-700 shadow-sm search-container">
                <div className="p-4">
                  <div className="relative">
                    <input
                      type="text"
                      className="w-full px-3 py-2 pl-10 text-sm rounded-md border border-gray-300 dark:border-gray-600 focus:outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      placeholder="ابحث عن دورة أو كتاب..."
                      value={searchQuery}
                      onChange={(e) => handleSearch(e.target.value)}
                      autoFocus
                    />
                    <div className="absolute inset-y-0 left-0 flex items-center pl-3">
                      <SearchIcon className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                    </div>
                  </div>
                  {searchQuery.length >= 2 && (
                    <div className="mt-4 space-y-2">
                      {isSearching ? (
                        <div className="text-center py-4">
                          <div className="inline-block animate-spin rounded-full h-6 w-6 border-2 border-gray-900 dark:border-white border-t-transparent"></div>
                        </div>
                      ) : searchResults.length > 0 ? (
                        searchResults.map((result) => (
                          <Link
                            key={result.id}
                            to={result.url}
                            className="block p-4 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg transition-colors"
                            onClick={() => setShowSearch(false)}
                          >
                            <div className="flex items-center space-x-4 space-x-reverse">
                              <div className="flex-shrink-0">
                                {result.type === 'course' ? (
                                  <BookOpenIcon className="h-6 w-6 text-primary-500" />
                                ) : (
                                  <CreditCardIcon className="h-6 w-6 text-primary-500" />
                                )}
                              </div>
                              <div>
                                <h4 className="text-sm font-medium text-gray-900 dark:text-white">{result.title}</h4>
                                <p className="text-xs text-gray-500 dark:text-gray-400">{result.type === 'course' ? 'دورة تدريبية' : 'كتاب'}</p>
                              </div>
                            </div>
                          </Link>
                        ))
                      ) : (
                        <div className="text-center py-4 text-gray-500 dark:text-gray-400">
                          لا توجد نتائج
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </nav>

    {/* دليل الاستخدام — GuideSlider Modal */}
    {showUsageModal && (
      <div className="fixed inset-0" style={{ zIndex: 9999 }}>
        <div 
          className="fixed inset-0 bg-black/40 backdrop-blur-sm" 
          onClick={() => setShowUsageModal(false)} 
        />
        <div className="fixed inset-0 flex items-center justify-center p-4 overflow-hidden">
          <div onClick={(e) => e.stopPropagation()}>
            <GuideSlider onClose={() => setShowUsageModal(false)} />
          </div>
        </div>
      </div>
    )}
  </>
);

// Arcade Embed Component
function ArcadeEmbed() {
  const arcadeIframeRef = useRef(null);

  useEffect(() => {
    function onArcadeIframeMessage(e) {
      if (e.origin !== 'https://demo.arcade.software' || !e.isTrusted) return;

      const arcadeIframe = arcadeIframeRef.current;
      if (!arcadeIframe || !arcadeIframe.contentWindow) return;

      if (e.data.event === 'arcade-init') {
        arcadeIframe.contentWindow.postMessage({event: 'register-popout-handler'}, '*');
      }
      
      if (e.data.event === 'arcade-popout-open') {
        arcadeIframe.style['position'] = 'fixed';
        arcadeIframe.style['z-index'] = '9999999';
      }

      if (e.data.event === 'arcade-popout-close') {
        arcadeIframe.style['position'] = 'absolute';
        arcadeIframe.style['z-index'] = 'auto';
      }
    }

    window.addEventListener('message', onArcadeIframeMessage);

    const arcadeIframe = arcadeIframeRef.current;
    if (arcadeIframe && arcadeIframe.contentWindow) {
      arcadeIframe.contentWindow.postMessage({event: 'register-popout-handler'}, '*');
    }
    
    return () => {
      if (arcadeIframe && arcadeIframe.contentWindow) {
        arcadeIframe.contentWindow.postMessage({event: 'unregister-popout-handler'}, '*');
      }

      window.removeEventListener('message', onArcadeIframeMessage);
    }
  }, [])

  return (
    <div style={{ position: 'relative', paddingBottom: 'calc(62.5% + 41px)', height: '0', width: '100%' }}>
      <iframe
        ref={arcadeIframeRef}
        src="https://demo.arcade.software/dLJqXgdbBSt6g0QBXDnj?embed&embed_mobile=modal&embed_desktop=inline&show_copy_link=true"
        title="شرح الاستخدام منصة دوز التعليمية"
        frameBorder="0"
        loading="lazy"
        allowFullScreen
        allow="clipboard-write"
        style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', colorScheme: 'light' }}
      />
    </div>
  )
}
}

export default Navbar;
